import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Card } from '../components/ui/card';
import DataTableShell from '../components/common/DataTableShell';
import { EventPagination } from '../components/events/EventPagination';
import { Button } from '../components/ui/button';
import { ChevronLeft, Copy, Check } from 'lucide-react';
import { checkpointsService, VerifyTxnResponse } from '../services/checkpointsService';
import { CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import { config } from '../lib/config';

interface TxnRow {
    id: number; // serial index starting from 1 for display
    txHash: string;
    blockNumber: number;
}

const IntegrityCheckerTxns: React.FC = () => {
    const { checkpointId = '1' } = useParams<{ checkpointId: string }>();
    const { state } = useLocation() as { state?: { checkpointId?: number; startBlock?: number; endBlock?: number } };
    const navigate = useNavigate();
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [rows, setRows] = useState<TxnRow[]>([]);
    const [totalRows, setTotalRows] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [loading, setLoading] = useState(false);
    const [verifying, setVerifying] = useState<string | null>(null); // txHash being verified
    const [verifyResult, setVerifyResult] = useState<Record<string, VerifyTxnResponse['data'] | null>>({});
    const [copiedHash, setCopiedHash] = useState<string | null>(null);

    useEffect(() => {
        const run = async () => {
            try {
                setLoading(true);
                const cid = Number(checkpointId);
                const res = await checkpointsService.getCheckpointTxns({
                    checkpointId: cid,
                    startBlock: state?.startBlock,
                    endBlock: state?.endBlock,
                    page: currentPage,
                    limit: itemsPerPage,
                });
                const mapped: TxnRow[] = res.txns.map((t, idx) => ({ id: (currentPage - 1) * itemsPerPage + idx + 1, txHash: t.txHash, blockNumber: t.blockNumber }));
                setRows(mapped);
                setTotalRows(res.totalCount);
                setTotalPages(res.totalPages);
            } catch (e) {
                console.error('Failed to load checkpoint txns', e);
                setRows([]);
                setTotalRows(0);
                setTotalPages(1);
            } finally {
                setLoading(false);
            }
        };
        run();
    }, [checkpointId, state?.startBlock, state?.endBlock, currentPage, itemsPerPage]);

    const goToPage = (p: number) => setCurrentPage(Math.min(Math.max(1, p), totalPages));
    const onItemsPerPageChange = (n: number) => {
        setItemsPerPage(n);
        setCurrentPage(1);
    };

    const truncate = (hash: string) => `${hash.slice(0, 12)}...${hash.slice(-8)}`;

    return (
        <div className="space-y-6">
            <div className="flex flex-col gap-2">
                <div>
                    <Button variant="outline" size="sm" onClick={() => navigate('/integrity-checker')} className="inline-flex items-center gap-2">
                        <ChevronLeft className="w-4 h-4" />
                        Back
                    </Button>
                </div>
                <div>
                    <h1 className="text-2xl font-semibold text-gray-900">Checkpoint #{checkpointId} Details</h1>
                    <p className="mt-1 text-sm text-gray-600">Transactions linked to the selected checkpoint • {totalRows} total</p>
                </div>
            </div>

            <Card className="overflow-hidden">
                <DataTableShell headers={[{ label: 'Sr No' }, { label: 'Tx Hash' }, { label: 'Block Number' }, { label: 'Actions', align: 'center' }]}>
                    {loading ? (
                        <tr>
                            <td colSpan={4} className="px-6 py-8 text-center text-gray-500">
                                Loading...
                            </td>
                        </tr>
                    ) : rows.length === 0 ? (
                        <tr>
                            <td colSpan={4} className="px-6 py-8 text-center text-gray-500">
                                No transactions found.
                            </td>
                        </tr>
                    ) : (
                        rows.map((row) => (
                            <tr key={`${checkpointId}-${row.id}`} className="hover:bg-gray-50 transition-colors">
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <span className="text-sm text-gray-900">{row.id}</span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="flex items-center gap-2">
                                        <span className="text-sm text-gray-900 font-mono">{truncate(row.txHash)}</span>
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                navigator.clipboard.writeText(row.txHash);
                                                setCopiedHash(row.txHash);
                                                setTimeout(() => setCopiedHash(null), 2000);
                                            }}
                                            className="p-1 hover:bg-gray-200 rounded transition-colors"
                                            title="Copy transaction hash"
                                        >
                                            {copiedHash === row.txHash ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4 text-gray-600" />}
                                        </button>
                                    </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <span className="text-sm text-gray-900">{row.blockNumber.toLocaleString()}</span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="flex items-center justify-center gap-2">
                                        <a href={`${config.eventsAppBaseUrl}/events/${row.txHash}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                                            View Txn
                                        </a>

                                        <span className="text-gray-300">|</span>
                                        <button
                                            type="button"
                                            className="text-blue-600 hover:underline disabled:text-gray-400"
                                            disabled={verifying === row.txHash}
                                            onClick={async () => {
                                                setVerifying(row.txHash);
                                                setVerifyResult((prev) => ({ ...prev, [row.txHash]: null }));
                                                try {
                                                    const res = await checkpointsService.verifyTxn(row.txHash);
                                                    setVerifyResult((prev) => ({ ...prev, [row.txHash]: res.data }));
                                                } catch (e) {
                                                    setVerifyResult((prev) => ({ ...prev, [row.txHash]: null }));
                                                } finally {
                                                    setVerifying(null);
                                                }
                                            }}
                                        >
                                            Verify Txn
                                        </button>
                                        <span style={{ display: 'inline-block', width: 24, height: 20, verticalAlign: 'middle' }}>
                                            {verifying === row.txHash ? (
                                                <Loader2 className="w-5 h-5 animate-spin text-gray-500" />
                                            ) : verifyResult[row.txHash] ? (
                                                verifyResult[row.txHash]?.verified ? (
                                                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                                                ) : (
                                                    <XCircle className="w-5 h-5 text-red-600" />
                                                )
                                            ) : (
                                                <span style={{ width: 20, height: 20, display: 'inline-block', visibility: 'hidden' }} />
                                            )}
                                        </span>
                                    </div>
                                </td>
                            </tr>
                        ))
                    )}
                </DataTableShell>

                <EventPagination currentPage={currentPage} totalPages={totalPages} totalEvents={totalRows} itemsPerPage={itemsPerPage} onPageChange={goToPage} onItemsPerPageChange={onItemsPerPageChange} />
            </Card>
        </div>
    );
};

export default IntegrityCheckerTxns;
